import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Taxes from "../classes/Taxes";
import Popup from "./Popup";
import MaterialIcons from "../icons/MaterialIcons";
import IconButton from "./IconButton";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import TaxesRequest from "../models/TaxesRequest";
import Button from "./Button";
import TaxRate from "../models/TaxRate";
import ListWrapper from "../utils/ListWrapper";
import LabelToggleView from "./LabelToggleView";
import AdminAddTaxesView from "./AdminAddTaxesView";
import TextView from "./TextView";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _AddTaxesButtonOnPressed = (d3eState: AdminConfigurationPageRefs) => void;

type _LabelToggleViewOnChanged = (
  text: boolean,
  d3eState: AdminConfigurationPageRefs
) => void;

export interface AdminConfigurationPageProps extends BaseUIProps {
  key?: string;
}
/// To store state data for AdminConfigurationPage
class AdminConfigurationPageRefs {
  public addTaxesButton: AddTaxesButtonState = new AddTaxesButtonState();
}

interface AddTaxesButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminConfigurationPageRefs;
  _onPressedAddTaxesButtonHandler?: _AddTaxesButtonOnPressed;
}

class AddTaxesButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _AddTaxesButtonWithState extends ObservableComponent<AddTaxesButtonWithStateProps> {
  addTaxesButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddTaxesButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addTaxesButton(): AddTaxesButtonState {
    return this.props.d3eState.addTaxesButton;
  }
  public get d3eState(): AdminConfigurationPageRefs {
    return this.props.d3eState;
  }
  public get _onPressedAddTaxesButtonHandler(): _AddTaxesButtonOnPressed {
    return this.props._onPressedAddTaxesButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("addTaxesButton", null, this.addTaxesButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["addTaxesButton", "addTaxesButton.", "addTaxesButton.hover"],
      this.rebuild
    );
  }
  public addTaxesButtonOnEnter(event): void {
    return this.addTaxesButton.setHover(true);
  }
  public addTaxesButtonOnExit(event): void {
    return this.addTaxesButton.setHover(false);
  }
  public dispose(): void {
    this.addTaxesButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.addTaxesButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.addTaxesButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.addTaxesButton.disable,
        onPressed: () => {
          this._onPressedAddTaxesButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "+ Add Taxes" }),
        onEnter: (event) => {
          this.addTaxesButtonOnEnter(event);
        },
        onExit: (event) => {
          this.addTaxesButtonOnExit(event);
        },
      }),
      className: "xe6",
    });
  }
}
function AddTaxesButtonWithState(props: AddTaxesButtonWithStateProps) {
  return React.createElement(_AddTaxesButtonWithState, props);
}

class _AdminConfigurationPageState extends ObservableComponent<AdminConfigurationPageProps> {
  d3eState: AdminConfigurationPageRefs = new AdminConfigurationPageRefs();
  taxList: Taxes = null;
  taxesList: Array<TaxRate> = ListWrapper.widget(this, "taxesList");
  tax: TaxRate = null;
  businessApprovelStatus: boolean = false;
  adminAddTaxesViewRefPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminConfigurationPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.computeTaxList();

    this.on(["taxList", "taxList.items"], this.computeTaxesList);

    this.computeTaxesList();

    this.computeBusinessApprovelStatus();

    this.on(
      [
        "businessApprovelStatus",
        "taxesList",
        "taxesList.country",
        "taxesList.jurisdiction",
        "taxesList.rate",
      ],
      this.rebuild
    );
  }
  public setTaxList(val: Taxes): void {
    let isValChanged: boolean = this.taxList !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("taxList", this.taxList, val);

    MessageDispatch.get().dispose(this.taxList);

    this.taxList = val;

    this.fire("taxList", this);
  }
  public computeTaxList = async (): Promise<void> => {
    try {
      this.setTaxList(
        await Query.get().getTaxes(
          UsageConstants.QUERY_GETTAXES_ADMINCONFIGURATIONPAGE_PROPERTIES_TAXLIST_COMPUTATION,
          new TaxesRequest({ country: "USA" }),
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeTaxList : " + exception.toString());

      this.setTaxList(null);
    }
  };
  public setTaxesList(val: Array<TaxRate>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.taxesList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("taxesList", this.taxesList, val);

    this.taxesList.clear();

    this.taxesList.addAll(val);

    this.fire("taxesList", this);
  }
  public addToTaxesList(val: TaxRate, index: number = -1): void {
    if (index === -1) {
      if (!this.taxesList.contains(val)) this.taxesList.add(val);
    } else {
      this.taxesList.remove(this.taxesList.elementAt(index));

      this.taxesList.add(val);
    }

    this.fire("taxesList", this, val, true);

    this.updateObservable("taxesList", null, val);
  }
  public removeFromTaxesList(val: TaxRate): void {
    this.taxesList.remove(val);

    this.fire("taxesList", this, val, false);

    this.removeObservable("taxesList", val);
  }
  public computeTaxesList = (): void => {
    try {
      this.setTaxesList(
        Array.from(this.taxList.items.isNotEmpty ? this.taxList.items : [])
      );
    } catch (exception) {
      console.log(" exception in computeTaxesList : " + exception.toString());

      this.setTaxesList([]);
    }
  };
  public setTax(val: TaxRate): void {
    let isValChanged: boolean = this.tax !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("tax", this.tax, val);

    this.tax = val;

    this.fire("tax", this);
  }
  public setBusinessApprovelStatus(val: boolean): void {
    let isValChanged: boolean = this.businessApprovelStatus !== val;

    if (!isValChanged) {
      return;
    }

    this.businessApprovelStatus = val;

    this.fire("businessApprovelStatus", this);
  }
  public computeBusinessApprovelStatus = async (): Promise<void> => {
    try {
      this.setBusinessApprovelStatus(
        await RPCServices.getConfigurationUtils().getAdminApproval()
      );
    } catch (exception) {
      console.log(
        " exception in computeBusinessApprovelStatus : " + exception.toString()
      );

      this.setBusinessApprovelStatus(false);
    }
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        TextView({
          data: "Configuration",
          style: new ui.TextStyle({
            fontSize: cStyle.tTextViewHeadlineTwoFontSizeOn,
            fontWeight: cStyle.tTextViewHeadlineTwoFontWeightOn,
          }),
          className: "xcb4 hc",
          key: "0",
        }),
        ui.Row({
          children: [
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                TextView({
                  data: "Platform Settings",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineThreeFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineThreeFontWeightOn,
                  }),
                  className: "x44 hc",
                  key: "0",
                }),
                ui.Container({
                  width: 700,
                  child: LabelToggleView({
                    label:
                      "Need admin approval for business or individual servce provider.",
                    value: this.businessApprovelStatus,
                    onChanged: (text) => {
                      this.labelToggleViewonChanged(text, this.d3eState);

                      this.onApprovelStatusChangeHandler(text, this.d3eState);
                    },
                  }),
                  key: "1",
                  className: "x79 hc",
                }),
                TextView({
                  data: "Payments",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineThreeFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineThreeFontWeightOn,
                  }),
                  className: "x790 hc",
                  key: "2",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
                      children: [
                        TextView({
                          data: "Taxes",
                          style: new ui.TextStyle({
                            fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                            fontWeight:
                              cStyle.tTextViewHeadlineFourFontWeightOn,
                          }),
                          className: "x1fb",
                          key: "0",
                        }),
                        AddTaxesButtonWithState({
                          d3eState: this.d3eState,
                          _onPressedAddTaxesButtonHandler:
                            this.onPressedAddTaxesButtonHandler,
                          key: "1",
                        }),
                      ],
                      className: "x4e hc h",
                      key: "0",
                    }),
                    ui.Table({
                      defaultColumnWidth: new ui.FlexColumnWidth(1),
                      columnWidths: {
                        0: new ui.FlexColumnWidth(1),
                        1: new ui.FlexColumnWidth(1),
                        2: new ui.FlexColumnWidth(1),
                        3: new ui.FlexColumnWidth(1),
                      },
                      children: [
                        ui.TableRow({
                          children: [
                            ui.TableCell({
                              child: TextView({
                                data: "Country",
                                style: new ui.TextStyle({
                                  fontSize:
                                    cStyle.tTextViewHeadlineFourFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewHeadlineFourFontWeightOn,
                                }),
                                className: "x424 hc",
                              }),
                              className: "hc",
                              key: "0",
                            }),
                            ui.TableCell({
                              child: TextView({
                                data: "Jurisdiction",
                                style: new ui.TextStyle({
                                  fontSize:
                                    cStyle.tTextViewHeadlineFourFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewHeadlineFourFontWeightOn,
                                }),
                                className: "xa9 hc",
                              }),
                              className: "hc",
                              key: "1",
                            }),
                            ui.TableCell({
                              child: TextView({
                                data: "Tax Rate",
                                style: new ui.TextStyle({
                                  fontSize:
                                    cStyle.tTextViewHeadlineFourFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewHeadlineFourFontWeightOn,
                                }),
                                className: "xb4e hc",
                              }),
                              className: "hc",
                              key: "2",
                            }),
                            ui.TableCell({
                              child: TextView({
                                data: "Actions",
                                style: new ui.TextStyle({
                                  fontSize:
                                    cStyle.tTextViewHeadlineFourFontSizeOn,
                                  fontWeight:
                                    cStyle.tTextViewHeadlineFourFontWeightOn,
                                }),
                                className: "x626 hc",
                              }),
                              className: "hc",
                              key: "3",
                            }),
                          ],
                          className: "xf59 hc",
                          key: "0",
                        }),
                        this.taxesList.expand((item) => [
                          ui.TableRow({
                            children: [
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: item.country,
                                  style: new ui.TextStyle({
                                    fontSize:
                                      cStyle.tTextViewHeadlineSixFontSizeOn,
                                    fontWeight:
                                      cStyle.tTextViewHeadlineSixFontWeightOn,
                                  }),
                                  className: "x59 hc",
                                }),
                                className: "hc",
                                key: "0",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: item.jurisdiction,
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "1",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: item.rate.toString(),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "2",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: ui.Row({
                                  children: [
                                    IconButton({
                                      icon: MaterialIcons.edit,
                                      color: cStyle.c4,
                                      hoverColor: cStyle.c1,
                                      onPressed: () => {
                                        this.addFileHandler(
                                          item,
                                          this.d3eState
                                        );
                                      },
                                      key: "0",
                                    }),
                                    ui.Container({ key: "1" }),
                                  ],
                                  className: "xaa96 hc h",
                                }),
                                className: "x32 hc h",
                                key: "3",
                              }),
                            ],
                            className: "x0bc2 hc h",
                            key: item?.ident,
                          }),
                        ]),
                      ],
                      className: "xd9 hc h",
                      key: "1",
                    }),
                  ],
                  className: "xa3 hc h",
                  key: "3",
                }),
              ],
              className: "x12 hc h",
              key: "0",
            }),
          ],
          className: "x409 hc h",
          key: "1",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminConfigurationPage xc41 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onPressedAddTaxesButtonHandler = (
    d3eState: AdminConfigurationPageRefs
  ): void => {
    this.setTax(new TaxRate());

    this.showAdminAddTaxesViewRef();
  };
  public addFileHandler = (
    item: TaxRate,
    d3eState: AdminConfigurationPageRefs
  ): void => {
    this.setTax(item);

    this.showAdminAddTaxesViewRef();
  };
  public onApprovelStatusChangeHandler = async (
    value: boolean,
    d3eState: AdminConfigurationPageRefs
  ): Promise<void> => {
    RPCServices.getConfigurationUtils().setAdminApproval(value);
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.taxList);

    this.adminAddTaxesViewRefPopup?.dispose();

    super.dispose();
  }
  public showAdminAddTaxesViewRef(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.adminAddTaxesViewRefPopup?.dispose();

    this.adminAddTaxesViewRefPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        height: 400,
        width: 400,
        child: AdminAddTaxesView({ obj: this.tax }),
        className: "x128 hc vc",
      }),
    });

    this.adminAddTaxesViewRefPopup.showPopup(this.context);
  }
  public hideAdminAddTaxesViewRef(): void {
    this.adminAddTaxesViewRefPopup?.dispose();
  }
  public labelToggleViewonChanged = (
    val: boolean,
    d3eState: AdminConfigurationPageRefs
  ): void => {
    this.setBusinessApprovelStatus(val);
  };
  public get addTaxesButton() {
    return this.d3eState.addTaxesButton;
  }
}
export default function AdminConfigurationPage(
  props: AdminConfigurationPageProps
) {
  return React.createElement(_AdminConfigurationPageState, props);
}
